import { useNavigate } from 'react-router-dom';
import { Package, Truck, UtensilsCrossed, Star, Clock, DollarSign, Heart, ChefHat, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { useOrder } from '@/contexts/OrderContext';

const LandingPage = () => {
  const navigate = useNavigate();
  const { setOrderType, MINIMUM_ORDER_DELIVERY } = useOrder();

  const handleOrderTypeSelection = (type) => {
    setOrderType(type);
    navigate('/menu');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black">
      {/* Hero Section */}
      <div className="min-h-screen flex flex-col items-center justify-center px-4 py-12">
        <div className="text-center mb-12 space-y-6">
          <div className="flex justify-center mb-6">
            <img 
              src="/spicifi-logo.png" 
              alt="SPICIFI - Perfect Blend of Cultures & Cuisine" 
              className="h-32 w-auto object-contain"
            />
          </div>
          
          <p className="text-2xl text-amber-200 font-['Inter']">
            Authentic South & North Indian Delicacies
          </p>
          
          <div className="text-amber-100 space-y-1 text-sm">
            <p>F55, GR-Floor, F Wing, Kailash Industrial Complex, Vikhroli West</p>
            <p>9444363303 / 9428626766</p>
          </div>
        </div>

        <div className="max-w-4xl w-full space-y-6">
          <h2 className="text-3xl font-bold text-center text-amber-400 mb-8 font-['Playfair_Display']">
            How would you like to order?
          </h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            <Card className="group cursor-pointer bg-gradient-to-br from-gray-800 to-gray-900 border-2 border-amber-500/30 hover:border-amber-500 transition-all duration-300 hover:scale-105" onClick={() => handleOrderTypeSelection('pickup')}>
              <div className="p-8 text-center space-y-6">
                <div className="flex justify-center">
                  <div className="bg-gradient-to-br from-orange-500 to-red-600 p-6 rounded-2xl">
                    <Package className="w-12 h-12 text-white" />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <h3 className="text-3xl font-bold text-amber-400 font-['Playfair_Display']">PICKUP</h3>
                  <p className="text-amber-200 text-lg">Collect from our restaurant</p>
                </div>
                
                <div className="space-y-3 text-amber-100">
                  <div className="flex items-center justify-center gap-2">
                    <span>No Minimum Order</span>
                  </div>
                  <div className="flex items-center justify-center gap-2">
                    <span>Packaging: ₹7/item</span>
                  </div>
                </div>
                
                <Button className="w-full bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white font-bold py-6">
                  Choose Pickup
                </Button>
              </div>
            </Card>

            <Card className="group cursor-pointer bg-gradient-to-br from-gray-800 to-gray-900 border-2 border-amber-500/30 hover:border-amber-500 transition-all duration-300 hover:scale-105" onClick={() => handleOrderTypeSelection('delivery')}>
              <div className="p-8 text-center space-y-6">
                <div className="flex justify-center">
                  <div className="bg-gradient-to-br from-orange-500 to-red-600 p-6 rounded-2xl">
                    <Truck className="w-12 h-12 text-white" />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <h3 className="text-3xl font-bold text-amber-400 font-['Playfair_Display']">DELIVERY</h3>
                  <p className="text-amber-200 text-lg">We deliver to your doorstep</p>
                </div>
                
                <div className="space-y-3 text-amber-100">
                  <div className="flex items-center justify-center gap-2">
                    <span>Minimum Order: ₹{MINIMUM_ORDER_DELIVERY}</span>
                  </div>
                  <div className="flex items-center justify-center gap-2">
                    <span>Packaging: ₹7/item</span>
                  </div>
                </div>
                
                <Button className="w-full bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white font-bold py-6">
                  Choose Delivery
                </Button>
              </div>
            </Card>
          </div>
        </div>

        {/* Our Specialities Section */}
        <div className="mt-16 max-w-4xl w-full">
          <Card className="bg-gradient-to-br from-gray-800 to-gray-900 border-2 border-amber-500/30 overflow-hidden">
            <div className="bg-gradient-to-r from-amber-600 to-orange-600 p-4">
              <h2 className="text-2xl font-bold text-white text-center font-['Playfair_Display']">
                Our Specialities
              </h2>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-gray-700/50 rounded-xl p-4 border border-amber-500/20">
                  <div className="flex items-start gap-3">
                    <span className="text-2xl">🍚</span>
                    <div>
                      <h3 className="text-amber-400 font-bold text-lg">Late Night Biryanis</h3>
                      <p className="text-amber-100 text-sm">We serve Veg & Non-Veg Biryanis from <span className="text-amber-400 font-semibold">12 AM to 3 AM</span> (Upcoming)</p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-gray-700/50 rounded-xl p-4 border border-amber-500/20">
                  <div className="flex items-start gap-3">
                    <span className="text-2xl">🍗</span>
                    <div>
                      <h3 className="text-amber-400 font-bold text-lg">Kilo Biryanis</h3>
                      <p className="text-amber-100 text-sm">Order our special <span className="text-amber-400 font-semibold">Kilo Biryanis</span> for parties and gatherings</p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-gray-700/50 rounded-xl p-4 border border-amber-500/20">
                  <div className="flex items-start gap-3">
                    <span className="text-2xl">🥘</span>
                    <div>
                      <h3 className="text-amber-400 font-bold text-lg">Bulk Orders</h3>
                      <p className="text-amber-100 text-sm"><span className="text-amber-400 font-semibold">Bulk Dry Veg, Tandoori & Non-Veg items</span> available on preorder</p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-gray-700/50 rounded-xl p-4 border border-amber-500/20">
                  <div className="flex items-start gap-3">
                    <span className="text-2xl">🍳</span>
                    <div>
                      <h3 className="text-amber-400 font-bold text-lg">South Indian Non-Veg Breakfast</h3>
                      <p className="text-amber-100 text-sm">Specialists in authentic <span className="text-amber-400 font-semibold">South Indian Non-Veg Breakfasts</span></p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="text-center pt-4 border-t border-amber-500/20">
                <p className="text-amber-200 text-sm">
                  For bulk orders & special requests, call us at <span className="text-amber-400 font-bold">9444363303 / 9428626766</span>
                </p>
              </div>
            </div>
          </Card>
        </div>

        <div className="mt-12">
          <Button 
            onClick={() => document.getElementById('about-section').scrollIntoView({ behavior: 'smooth' })}
            variant="ghost"
            className="text-amber-400 hover:text-amber-300"
          >
            Learn More About Us ↓
          </Button>
        </div>
      </div>

      {/* About Section */}
      <div id="about-section" className="py-20 px-4 border-t-2 border-amber-500/20">
        <div className="max-w-6xl mx-auto space-y-20">
          
          {/* Welcome Section */}
          <div className="text-center space-y-6">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-amber-500 to-orange-600 rounded-full mb-4">
              <Sparkles className="w-10 h-10 text-white" />
            </div>
            <h2 className="text-5xl sm:text-6xl font-bold bg-gradient-to-r from-yellow-400 via-amber-500 to-orange-600 bg-clip-text text-transparent font-['Playfair_Display']">
              Welcome to SPICIFI
            </h2>
            <p className="text-3xl text-amber-300 font-semibold">
              Spice it up with SPICIFI!
            </p>
            <p className="text-xl text-amber-100 max-w-3xl mx-auto leading-relaxed">
              At SPICIFI, we bring together the rich flavors of North Indian, South Indian & Chinese cuisine — serving both Veg & Non-Veg delights crafted with passion and authenticity.
            </p>
          </div>

          {/* Core Values */}
          <div className="text-center space-y-8">
            <p className="text-2xl text-amber-200 font-semibold">
              We believe great food should be:
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto">
              {[
                { icon: Sparkles, text: 'Fresh' },
                { icon: Heart, text: 'Flavorful' },
                { icon: Clock, text: 'Fast' },
                { icon: DollarSign, text: 'Affordable' }
              ].map((item, idx) => (
                <Card key={idx} className="p-6 bg-gradient-to-br from-gray-800 to-gray-900 border-2 border-amber-500/30 hover:border-amber-500 transition-all">
                  <item.icon className="w-8 h-8 mx-auto mb-3 text-amber-400" />
                  <p className="text-lg font-bold text-amber-200">{item.text}</p>
                </Card>
              ))}
            </div>
            <p className="text-xl text-amber-100 font-semibold">
              And that's exactly what we deliver — every single time.
            </p>
          </div>

          {/* Our Story */}
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <h3 className="text-4xl font-bold text-amber-400 font-['Playfair_Display']">
                Our Story
              </h3>
              <div className="h-1 w-20 bg-gradient-to-r from-amber-500 to-orange-600"></div>
              <p className="text-lg text-amber-100 leading-relaxed">
                SPICIFI was created with one simple mission — to serve restaurant-quality food that satisfies your cravings in minutes.
              </p>
              <p className="text-lg text-amber-100 leading-relaxed">
                From traditional Indian thalis to spicy Chinese favorites, our menu is designed to suit every taste and every mood.
              </p>
              <p className="text-lg text-amber-100 leading-relaxed">
                Whether you're dining with family, ordering from office, or craving a late-night meal — <span className="text-amber-400 font-bold">SPICIFI is your go-to place.</span>
              </p>
            </div>
            <div className="relative">
              <div className="bg-gradient-to-br from-amber-500/20 to-orange-600/20 rounded-3xl p-8 border-2 border-amber-500/30">
                <ChefHat className="w-32 h-32 mx-auto text-amber-400 mb-4" />
                <p className="text-center text-2xl font-bold text-amber-300">
                  Crafted with Passion
                </p>
              </div>
            </div>
          </div>

          {/* What We Serve */}
          <div className="space-y-12">
            <div className="text-center">
              <h3 className="text-4xl font-bold text-amber-400 font-['Playfair_Display'] mb-4">
                What We Serve
              </h3>
              <div className="h-1 w-20 bg-gradient-to-r from-amber-500 to-orange-600 mx-auto"></div>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { emoji: '🍛', title: 'North Indian Specials', desc: 'Rich gravies, buttery curries, soft rotis, and aromatic biryanis.' },
                { emoji: '🍲', title: 'South Indian Favorites', desc: 'Crispy dosas, fluffy idlis, flavorful sambhar & chutneys.' },
                { emoji: '🥡', title: 'Chinese Delights', desc: 'Noodles, fried rice, Manchurian & Indo-Chinese specialties.' },
                { emoji: '🥗', title: 'Veg & Non-Veg', desc: 'Carefully prepared with quality ingredients and perfect spices.' }
              ].map((item, idx) => (
                <Card key={idx} className="p-6 bg-gradient-to-br from-gray-800 to-gray-900 border-2 border-amber-500/30 hover:border-amber-500 hover:scale-105 transition-all">
                  <div className="text-5xl mb-4 text-center">{item.emoji}</div>
                  <h4 className="text-xl font-bold text-amber-400 mb-3 text-center font-['Playfair_Display']">
                    {item.title}
                  </h4>
                  <p className="text-amber-100 text-center text-sm leading-relaxed">
                    {item.desc}
                  </p>
                </Card>
              ))}
            </div>
          </div>

          {/* Why Choose Us */}
          <div className="bg-gradient-to-br from-amber-500/10 to-orange-600/10 rounded-3xl p-12 border-2 border-amber-500/30">
            <h3 className="text-4xl font-bold text-center text-amber-400 font-['Playfair_Display'] mb-8">
              Why Choose SPICIFI?
            </h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                { icon: Sparkles, text: 'Freshly Prepared Food' },
                { icon: Star, text: 'Hygienic Kitchen Standards' },
                { icon: Clock, text: 'Quick Service' },
                { icon: DollarSign, text: 'Pocket-Friendly Pricing' },
                { icon: Heart, text: 'Perfect for Family Orders' },
                { icon: UtensilsCrossed, text: 'Restaurant Quality' }
              ].map((item, idx) => (
                <div key={idx} className="flex items-center gap-4 bg-gray-900/50 p-4 rounded-xl border border-amber-500/20">
                  <item.icon className="w-8 h-8 text-amber-400 flex-shrink-0" />
                  <p className="text-amber-100 font-semibold">{item.text}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Our Promise */}
          <div className="text-center space-y-8 py-12">
            <h3 className="text-4xl font-bold text-amber-400 font-['Playfair_Display']">
              Our Promise
            </h3>
            <div className="h-1 w-20 bg-gradient-to-r from-amber-500 to-orange-600 mx-auto"></div>
            <p className="text-2xl text-amber-200 max-w-3xl mx-auto leading-relaxed">
              At SPICIFI, we don't just serve food — we serve <span className="text-amber-400 font-bold">happiness on every plate.</span>
            </p>
            <div className="space-y-4 max-w-2xl mx-auto">
              <p className="text-xl text-amber-100">
                Your taste buds deserve something special.
              </p>
              <p className="text-xl text-amber-100 font-semibold">
                And we're here to make every meal memorable. 🍽️
              </p>
            </div>
            
            <div className="pt-8">
              <Button 
                onClick={() => handleOrderTypeSelection('delivery')}
                className="bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white font-bold py-6 px-12 text-xl rounded-xl shadow-2xl"
              >
                Order Now
              </Button>
            </div>
          </div>

        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gradient-to-r from-gray-900 to-black border-t-2 border-amber-500/30 py-8 mt-20">
        <div className="max-w-7xl mx-auto px-4 text-center space-y-4">
          <h3 className="text-2xl font-bold text-amber-400 font-['Playfair_Display']">SPICIFI</h3>
          <p className="text-amber-200">F55, GR-Floor, F Wing, Kailash Industrial Complex, Vikhroli West, Mumbai 400079</p>
          <p className="text-amber-200">📞 9444363303 / 9428626766</p>
          <div className="pt-4 text-gray-400 text-sm">
            <p>© 2026 SPICIFI. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* WhatsApp Floating Button */}
      <a
        href="https://wa.me/919444363303?text=Hi%20SPICIFI!%20I%20need%20help%20with%20my%20order."
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 z-50 flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white px-4 py-3 rounded-full shadow-2xl transition-all duration-300 hover:scale-105 group"
        data-testid="whatsapp-help-btn"
      >
        <svg 
          viewBox="0 0 24 24" 
          className="w-6 h-6 fill-current"
        >
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
        </svg>
        <span className="font-semibold text-sm">Quick Help</span>
      </a>
    </div>
  );
};

export default LandingPage;
